import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApiKey } from 'contexts/ApiKeyContext';
import s from './ApiPage.module.css';

const API_DOCS_PLACEHOLDER_URL = 'https://docs.quadratik.ai/docs';

const ApiPage = () => {
    const { user, loading } = useAuth();
    const { plaintextKey, prefix, hasKey, loading: loadingKeyMeta, createOrRotate } = useApiKey();
    const history = useHistory();
    const [keyRevealed, setKeyRevealed] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!loading && !user) {
            history.replace('/loginsignup');
        }
    }, [loading, user, history]);

    if (loading) {
        return (
            <div className={s.ApiPage}>
                <div className={s.inner}>Loading...</div>
            </div>
        );
    }

    if (!user) return null;

    let displayKey = null;
    if (plaintextKey) {
        displayKey = keyRevealed
            ? plaintextKey
            : `${plaintextKey.slice(0, 8)}${'•'.repeat(24)}${plaintextKey.slice(-4)}`;
    } else if (hasKey && prefix) {
        displayKey = `${prefix}${'•'.repeat(24)}`;
    }

    const handleCreateOrRotate = async () => {
        const result = await createOrRotate();
        if (!result.ok) {
            console.error('[ApiPage] Error creating/rotating API key:', result.error);
            return;
        }
        setKeyRevealed(true);
    };

    const handleCopy = () => {
        if (!plaintextKey) return;
        navigator.clipboard.writeText(plaintextKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const openApiDocs = () => {
        window.open(API_DOCS_PLACEHOLDER_URL, '_blank');
    };

    return (
        <div className={s.ApiPage}>
            <div className={s.inner}>
                <img src="/logo.png" alt="Company logo" className={s.logo} />
                <h1 className={s.header}>API</h1>
                <p className={s.subHeader}>Manage your API key and access the API documentation.</p>

                <section className={s.section}>
                    <h2 className={s.sectionTitle}>API Key</h2>
                    <div className={s.keyCard}>
                        {loadingKeyMeta ? (
                            <p className={s.noKeyText}>Checking your API key...</p>
                        ) : displayKey ? (
                            <>
                                <div className={s.keyRow}>
                                    <code className={s.keyValue}>{displayKey}</code>
                                    {plaintextKey && (
                                        <button
                                            type="button"
                                            className={s.revealBtn}
                                            onClick={() => setKeyRevealed(!keyRevealed)}
                                            title={keyRevealed ? 'Hide key' : 'Show full key'}
                                        >
                                            {keyRevealed ? 'Hide' : 'Show'}
                                        </button>
                                    )}
                                </div>
                                <div className={s.buttonRow}>
                                    <button
                                        type="button"
                                        className={s.copyBtn}
                                        onClick={handleCopy}
                                        disabled={!plaintextKey}
                                        title={plaintextKey ? 'Copy full key' : 'Rotate to generate a new key to copy'}
                                    >
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                    <button type="button" className={s.rotateBtn} onClick={handleCreateOrRotate}>
                                        Rotate key
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className={s.noKeyText}>No API key yet. Create one to use the API.</p>
                                <button type="button" className={s.createBtn} onClick={handleCreateOrRotate}>
                                    Create API key
                                </button>
                            </>
                        )}
                    </div>
                </section>

            <div className={s.divider} />

            <section className={s.section}>
                <h2 className={s.sectionTitle}>Documentation</h2>
                <button type="button" className={s.docsBtn} onClick={openApiDocs}>
                    View API docs
                </button>
            </section>
            </div>
        </div>
    );
};

export default ApiPage;
